import type React from "react";
import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { colorsPalettes, setFont } from "@similarweb/styles";
import {
    ChipDownContainer,
    Dropdown,
    DropdownButton,
    EllipsisDropdownItem,
    Button,
    SimpleChipItemWrap,
} from "@similarweb/ui-components";

import { ETiers, getTiersMeta } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/Constants";
import { useRankingDistributionTableTopContext } from "app/pages/keyword-research/website-analysis/pages/ranking-distribution/useRankingDistributionTableTopContext";
import { safeJsonParse } from "app/UtilitiesAndConstants/UtilityFunctions/safeJsonParse";
import { AdvancedFilterButton } from "pro-features/components/filtersPanel/src/filtersPanel";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { DomainCell } from "app/components/React/Table/cells";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { FilterItemButton } from "app/pages/website-analysis/traffic-sources/search/components/filters/WebsiteKeywordsPageFiltersStyledComponents";
import { FlexRow } from "app/pages/website-analysis/website-content/leading-folders/components/Tab";
import { allTrackers } from "app/services/track/track";

const Separator = styled.hr<{ preventDefault?: boolean }>`
    margin: 0;
`;

const ModalTitle = styled.div`
    ${setFont({ $color: colorsPalettes.carbon[500], $size: 20, $weight: 500 })};
    margin-bottom: 36px;
`;
const ModalSubTitle = styled.div`
    ${setFont({ $color: colorsPalettes.carbon[400], $size: 16, $weight: 400 })};
    margin-bottom: 16px;
`;

const ModalRowStyle = styled.div`
    display: grid;
    grid-template-columns: 43% 57%;
    padding-bottom: 16px;
`;

const ModalStyle = styled.div`
    padding: 24px 20px 16px 24px;
`;

const ModalFooterStyle = styled.div`
    display: flex;
    justify-content: flex-end;
`;
const CloseButton = styled(Button)`
    margin-right: 8px;
`;
const EndTextStyle = styled.div`
    height: 20px;
    width: 30px;
    font-size: 14px;
    font-weight: 400;
`;

export const getAggregatedTiers = () => {
    const prefix = translate("ranking.distribution.filters.position.tier.prefix");
    return [
        {
            text: `${prefix} ${translate("ranking.distribution.filters.position.tier.1")}`,
            value: "-3",
            tiers: [ETiers.TIER1],
        },
        {
            text: `${prefix} ${translate("ranking.distribution.filters.position.tier.2")}`,
            value: "-10",
            tiers: [ETiers.TIER1, ETiers.TIER2],
        },
        {
            text: `${prefix} ${translate("ranking.distribution.filters.position.tier.3")}`,
            value: "-20",
            tiers: [ETiers.TIER1, ETiers.TIER2, ETiers.TIER3],
        },
    ];
};

export const getSingleTiers = () => {
    const tiersMeta = getTiersMeta();
    return [
        {
            text: tiersMeta[ETiers.TIER1].text,
            value: tiersMeta[ETiers.TIER1].id,
            tiers: [ETiers.TIER1],
            compareTableTooltipText: tiersMeta[ETiers.TIER1].compareTableTooltipText,
        },
        {
            text: tiersMeta[ETiers.TIER2].text,
            value: tiersMeta[ETiers.TIER2].id,
            tiers: [ETiers.TIER2],
            compareTableTooltipText: tiersMeta[ETiers.TIER2].compareTableTooltipText,
        },
        {
            text: tiersMeta[ETiers.TIER3].text,
            value: tiersMeta[ETiers.TIER3].id,
            tiers: [ETiers.TIER3],
            compareTableTooltipText: tiersMeta[ETiers.TIER3].compareTableTooltipText,
        },
        {
            text: tiersMeta[ETiers.TIER4].text,
            value: tiersMeta[ETiers.TIER4].id,
            tiers: [ETiers.TIER4],
            compareTableTooltipText: tiersMeta[ETiers.TIER4].compareTableTooltipText,
        },
    ];
};

export const RankingTierFilterV2: React.FC = () => {
    const ANY_RANK = translate("ranking.distribution.filters.any.rank");
    const {
        tableFilters: { ranking },
        onRankingTierChange,
        isCompare,
    }: any = useRankingDistributionTableTopContext();
    const { params } = useCurrentStateAndParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const aggregatedTiers = getAggregatedTiers();
    const items = [...aggregatedTiers, ...getSingleTiers()];
    const urls = safeJsonParse(params.pageFilter, []);
    const getSelectedItems = () => {
        let positionName, positionValue;
        const selectedIds = [];
        if (ranking) {
            const filterInArray = ranking.split(",");
            filterInArray.map((rank) => {
                [positionName, positionValue] = rank.split(":");
                const selected = items.find((item) => item.value === positionValue);
                const site = urls.find((site) => site.url === positionName);
                const siteIndex = urls.findIndex(
                    (item) => item.url === site.url && item.searchType === site.searchType,
                );
                selectedIds[siteIndex] = { name: site?.url, value: selected };
            });
        }
        return selectedIds;
    };
    const [selectedItems, setSelectedItems] = useState(getSelectedItems());
    const [selectedModalItems, setSelectedModalItems] = useState(selectedItems);

    useEffect(() => {
        const selected = getSelectedItems();
        setSelectedItems(selected);
        setSelectedModalItems(selected);
    }, [ranking]);

    const onToggle = (isOpen) => {
        if (isOpen) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "open Positions filter");
        }
    };
    const onTierClick = () => {
        allTrackers.dispatchTrackEvent("Drop Down", "open", "open compare position filter");
    };

    const getItemComponent = (item) => {
        return (
            <EllipsisDropdownItem
                id={item.value}
                key={item.value}
            >
                {translate(item.text)}
            </EllipsisDropdownItem>
        );
    };
    const CUSTOM_MODAL_STYLES = {
        content: { padding: 0, width: 400, border: 0 },
    };
    const itemsComponent = [
        ...aggregatedTiers.map(getItemComponent),
        <Separator
            key="separator"
            preventDefault={true}
        />,
        ...getSingleTiers().map(getItemComponent),
    ];

    const modalItemsComponent = [
        {
            text: ANY_RANK,
            value: ANY_RANK,
            tiers: [ETiers.TIER1],
        },
        ...itemsComponent,
    ];

    const dropdownChildren = (index) => {
        const buttonText = selectedModalItems[index] ? selectedModalItems[index].value.text : ANY_RANK;
        return [
            <DropdownButton
                key="existingQuery"
                width={200}
                height={40}
            >
                {buttonText}
            </DropdownButton>,
            ...modalItemsComponent,
        ];
    };

    const onModalClick = (index) => (newItem) => {
        if (newItem.text === ANY_RANK) {
            const selectedIds = [...selectedModalItems];
            selectedIds.splice(index, 1);
            setSelectedModalItems(selectedIds);
        } else {
            const selected = items.find((item) => item.value === newItem.id);
            const selectedIds = [...selectedModalItems];
            selectedIds[index] = { name: urls[index]?.url, value: selected };
            setSelectedModalItems(selectedIds);
        }
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "click",
            `open website position dropdown/${urls[index]?.url || ""}/${newItem.id || ""}`,
        );
    };

    const onApplyModalClick = () => {
        onRankingChange(selectedModalItems);
        setSelectedItems(selectedModalItems);
        setIsModalOpen(false);
        allTrackers.dispatchTrackEvent("Filter Table", "click", "Filters/Positions In Compare");
    };
    const onCloseModalClick = () => {
        setSelectedModalItems(selectedItems);
        setIsModalOpen(false);
    };

    const onRankingChange = (rankingSelected) => {
        const rankingFilter = [];
        let rankingText;
        if (urls.length > 1) {
            rankingSelected.forEach((item, index) => {
                if (item) {
                    if (item.value.value === ANY_RANK) {
                        return;
                    } else {
                        rankingFilter[item.name] = item.value.value;
                        rankingText = Object.keys(rankingFilter)
                            .map((name) => `${name}:${rankingFilter[name]}`)
                            .join(",");
                    }
                }
            });
        } else {
            rankingFilter.push(selectedModalItems[0]?.value?.value);
            rankingText = rankingFilter[0] ? rankingFilter[0] : "";
        }
        onRankingTierChange(rankingText, true);
    };

    const modalRow = (site, index) => {
        const selectedIds = selectedModalItems[index] ? { [selectedModalItems[index].value.value]: true } : {};
        return (
            <ModalRowStyle>
                <FlexRow>
                    <EndTextStyle>
                        {index > 0 ? translate("ranking.distribution.compare.filters.and") : ""}
                    </EndTextStyle>
                    <DomainCell field={site.url} />
                </FlexRow>
                <Dropdown
                    onClick={onModalClick(index)}
                    buttonWidth={212}
                    closeOnItemClick={true}
                    selectedIds={selectedIds}
                    itemsComponent={EllipsisDropdownItem}
                    dropdownPopupHeight={300}
                >
                    {dropdownChildren(index)}
                </Dropdown>
            </ModalRowStyle>
        );
    };

    const isAllCompetitorsTheSameFilter = (filters) => {
        const first = filters[0];
        const isDifferent = filters.filter((item) => item?.value !== first?.value).length > 0;
        return !isDifferent;
    };

    const numberOfCompetitorSelected = () => {
        const selected = [];
        selectedItems.forEach((filter) => {
            if (filter) {
                const [filterName, filterValue] = filter.value.text.split(" ");
                const value = filterName === "Positions" ? filterValue : filter.value.text;
                selected.push({
                    name: filter.name,
                    value: value,
                });
            }
        });
        return selected;
    };
    const getFilterButtonText = () => {
        if (selectedItems.length === 0) {
            return translate("ranking.distribution.filters.position");
        }
        const selected = numberOfCompetitorSelected();
        if (selected.length > 0) {
            if (selected.length !== urls.length) {
                if (selected.length === 1) {
                    return translate("ranking.distribution.filters.position.1.selected", {
                        name: selected[0].name,
                        value: selected[0].value,
                    });
                }
                if (isAllCompetitorsTheSameFilter(selected)) {
                    return translate("ranking.distribution.filters.position.selected", {
                        name: selected[0].name,
                        value: selected[0].value,
                        number: selected.length - 1,
                    });
                } else {
                    return translate("ranking.distribution.filters.position.custom");
                }
            } else {
                if (isAllCompetitorsTheSameFilter(selected)) {
                    return translate("ranking.distribution.filters.position.all.selected", {
                        value: selected[0].value,
                    });
                } else {
                    return translate("ranking.distribution.filters.position.custom");
                }
            }
        } else {
            return translate("ranking.distribution.filters.position");
        }
    };

    const onClearRankingFilter = (e) => {
        e.stopPropagation();
        onRankingChange([]);
    };
    const onRankingChangeSingleMode = (item) => {
        const rankingFilter = [];
        const site = urls[0].url;
        rankingFilter[site] = item.id;
        const rankingText = Object.keys(rankingFilter)
            .map((name) => `${name}:${rankingFilter[name]}`)
            .join(",");
        onRankingTierChange(rankingText, false);
    };
    const selectedIds = selectedModalItems[0] ? { [selectedModalItems[0].value.value]: true } : {};
    return (
        <div>
            {isCompare ? (
                <FilterItemButton>
                    <span onClick={() => setIsModalOpen(!isModalOpen)}>
                        {selectedModalItems.length > 0 ? (
                            <SimpleChipItemWrap
                                text={getFilterButtonText()}
                                onCloseItem={onClearRankingFilter}
                            />
                        ) : (
                            <span onClick={onTierClick}>
                                <AdvancedFilterButton>
                                    <I18n>{"ranking.distribution.filters.position"}</I18n>
                                </AdvancedFilterButton>
                            </span>
                        )}
                    </span>
                </FilterItemButton>
            ) : (
                <ChipDownContainer
                    width={220}
                    onClick={onRankingChangeSingleMode}
                    onToggle={onToggle}
                    selectedText={selectedModalItems[0] ? translate(selectedModalItems[0].value.text) : null}
                    onCloseItem={() => onRankingTierChange(null)}
                    buttonText={translate("ranking.distribution.filters.position")}
                    selectedIds={selectedIds}
                    noResultsMessage={translate("dropdown.no.results")}
                >
                    {itemsComponent}
                </ChipDownContainer>
            )}
            <ProModal
                isOpen={isModalOpen}
                showCloseIcon={false}
                customStyles={CUSTOM_MODAL_STYLES}
                shouldCloseOnOverlayClick={true}
            >
                <ModalStyle>
                    <ModalTitle>{translate("ranking.filter.modal.title")}</ModalTitle>
                    <ModalSubTitle>{translate("ranking.filter.modal.subtitle")}</ModalSubTitle>
                    {urls.map(modalRow)}
                    <ModalFooterStyle>
                        <CloseButton
                            onClick={onCloseModalClick}
                            type={"flat"}
                        >
                            {translate("ranking.filter.modal.close.button")}
                        </CloseButton>
                        <Button
                            onClick={onApplyModalClick}
                            type={"primary"}
                        >
                            {translate("ranking.filter.modal.apply.button")}
                        </Button>
                    </ModalFooterStyle>
                </ModalStyle>
            </ProModal>
        </div>
    );
};
